/**
 * @fileoverview Approve/Reject review request mutation hook.
 * @description Approves or rejects a review request with the `approvalVersion` the dialog loaded
 * and showed as X-Approval-Version, refreshing every sales query once it succeeds. A request that
 * changed since (re-routed, decided, withdrawn) is refused (`412` / `409`) and shown for review
 * rather than decided blindly (3.5.11.0). The command's Idempotency-Key outlives the dialog (see
 * useIdempotentMutation).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import {
  approveApprovalRequest,
  rejectApprovalRequest,
} from '@/features/sales/services/approvals.service'

import type {
  ApprovalDecisionResult,
  RejectApprovalRequest,
} from '@/features/sales/types/approval.types'

/** @description What to decide: approve (with an optional note) or reject (with a reason). */
export type ApprovalDecisionInput =
  | {
      approvalRequestId: string
      approvalVersion: string
      decision: 'approve'
      reviewNote: string | null
    }
  | {
      approvalRequestId: string
      approvalVersion: string
      decision: 'reject'
      rejection: RejectApprovalRequest
    }

/**
 * @description Approves or rejects a pending review request. The command's idempotency key outlives
 * the dialog, so a retry after an unknown outcome replays it.
 * @param {object} [options] - What follows a success.
 * @param {(result: ApprovalDecisionResult, input: ApprovalDecisionInput) => void}
 * [options.onSuccess] - Called once decided, even when the dialog has unmounted since.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with an
 * ApprovalDecisionInput).
 */
export function useDecideApprovalRequest({
  onSuccess,
}: {
  onSuccess?: ((result: ApprovalDecisionResult, input: ApprovalDecisionInput) => void) | undefined
} = {}) {
  return useIdempotentMutation<ApprovalDecisionInput, ApprovalDecisionResult>({
    action: 'decide-approval',
    /**
     * @description Scopes the key to the request and the decision made, not the version it is sent against.
     * @param {ApprovalDecisionInput} input - The decision's input.
     * @returns {object} The request id and the decision, with its note or reason.
     */
    scopeOf: (input) =>
      input.decision === 'approve'
        ? { id: input.approvalRequestId, decision: input.decision, reviewNote: input.reviewNote }
        : { id: input.approvalRequestId, decision: input.decision, rejection: input.rejection },
    /**
     * @description Approves or rejects the request under the key it is given.
     * @param {ApprovalDecisionInput} input - The request, its version and the decision.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<ApprovalDecisionResult>} The decision.
     */
    send: (input, key) =>
      input.decision === 'approve'
        ? approveApprovalRequest(
            input.approvalRequestId,
            input.approvalVersion,
            input.reviewNote,
            key,
          )
        : rejectApprovalRequest(
            input.approvalRequestId,
            input.approvalVersion,
            input.rejection,
            key,
          ),
    onSuccess,
  })
}
